import BgImg from '@assets/jpg/signup-bgs/step-2.jpeg';
import { Button, Form } from '@components/ui';
import { AuthTitleSC } from '@pages/auth/auth.styled';
import AuthLayout from '@pages/auth/components/auth-layout';
import Header from '@pages/auth/components/header';
import { resendVerificationEmail, verifyEmail } from '@services/external/auth.api';
import { FormikHelpers } from 'formik';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import ReactCodeInput from 'react-verification-code-input';
import { initialValues, TFormValues, validationSchema } from './reset-password-code.schema';
import { ResetPasswordCodeSC } from './reset-password-code.styled';

const ResetPasswordCode: React.FunctionComponent = () => {
  const { state } = useLocation<{ recoveryToken: string; email: string }>();
  const history = useHistory();

  const [loading, setLoading] = React.useState(false);
  const [isEmailResent, setIsEmailResent] = React.useState(false);

  const handleEmailResend = React.useCallback(async () => {
    setLoading(true);
    try {
      await resendVerificationEmail(state.recoveryToken);
      setIsEmailResent(true);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [state.recoveryToken]);

  const onSubmit = React.useCallback(
    async ({ emailCode }: TFormValues, helpers: FormikHelpers<TFormValues>) => {
      setLoading(true);
      try {
        const response = await verifyEmail(emailCode, state.recoveryToken);
        history.replace('/reset-password-new', {
          recoveryToken: 'recoveryToken' in response.data ? response.data.recoveryToken : '',
        });
      } catch (error: any) {
        if (error.data && error.data.message_code === 'recovery_wrong_code') {
          helpers.setErrors({ emailCode: 'forgotten_password_code.recovery_wrong_code' });
        } else {
          helpers.setErrors({ emailCode: 'forgotten_password_code.default_error' });
        }
      } finally {
        setLoading(false);
      }
    },
    [state?.recoveryToken],
  );

  React.useEffect(() => {
    if (!state.recoveryToken) {
      history.replace('/login');
    }
  }, [state?.recoveryToken]);

  const [, formOptions] = Form.useForm({
    initialValues,
    validationSchema,
    onSubmit,
  });

  return (
    <AuthLayout backgroundImage={BgImg}>
      <div className="form-wrapper forgotten">
        <Header textId="forgotten_password_code.title" />
        <div className="form">
          <ResetPasswordCodeSC>
            <AuthTitleSC>
              <FormattedMessage id="forgotten_password_code.header" />
            </AuthTitleSC>
            <p className="insert-code-text">
              <FormattedMessage
                id="reg.step_2.email"
                values={{
                  email: <span className="forgotten_password_code.description">{state.email}</span>,
                }}
              />
            </p>
            <Form {...formOptions} showFeedback={false}>
              <Form.Field name="emailCode" getValueFromEvent={(v) => v}>
                <ReactCodeInput
                  type="number"
                  fields={6}
                  className="verification-code-input"
                  required
                />
              </Form.Field>
              <Button
                type="submit"
                loading={loading}
                className="submit-button-code"
                style={{ marginTop: 16 }}
              >
                <FormattedMessage id={'forgotten_password_code.btn'} />
              </Button>
            </Form>
            <div className="resend-email" onClick={handleEmailResend} role="button">
              <span className="resend-email_txt">
                <FormattedMessage
                  id={isEmailResent ? 'reg.step_2.resend_email_success' : 'reg.step_2.no_email'}
                />
              </span>
              <span className="resend-email_btn" role="button">
                <FormattedMessage id="reg.step_2.resend_email" />
              </span>
            </div>
          </ResetPasswordCodeSC>
        </div>
      </div>
    </AuthLayout>
  );
};

export default React.memo(ResetPasswordCode);
